import type { PublicCurpParseResponse } from "./api";

// Revisión de CURP vs nombre (Eduardo 2026-10-01): ESPEJO de
// backend/app/modules/registrations/curp_classifier.py (`fold`, `tokens`,
// `code_letters`, `letters_of`, `birthdate_from_curp`, `sex_from_curp` y
// `classify`). El formulario público lo usa EN VIVO: la CURP casi siempre se
// sube antes de escribir el nombre, y cambiar de hijo conserva el archivo.
//
// No reimplementa la lectura del PDF ni `split_curp_name`: el nombre ya
// partido llega del servidor (`name_split` de parse-curp, partido con la clave
// como autoridad). Los dos lados prueban los MISMOS vectores
// (curpReviewVectors.json = backend/app/tests/data/curp_review_vectors.json):
// si cambias una regla aquí, cámbiala allá y agrega el caso a los vectores.
//
// Estados: coincide (`matches`), parecido (`similar`, amarillo), parece de
// otra persona (`other_person`, rojo), no se pudo leer (`unreadable`, gris).
// La revisión AVISA, nunca bloquea.

export type CurpStatus = "matches" | "similar" | "other_person" | "unreadable";

/** Nombre impreso ya partido (como `name` en el JSON guardado del backend). */
export interface CurpNameParts {
  given: string | null;
  paternal: string | null;
  maternal: string | null;
}

/** Lo que dice el PDF (mismas llaves que `read_curp_text` / el JSON guardado). */
export interface CurpFacts {
  reading: "ok" | "unreadable";
  reason?: string | null;
  name?: CurpNameParts | null;
  /** Las 7 letras del nombre dentro de la clave (posiciones 0-3 y 13-15). */
  letters?: string | null;
  birthdate?: string | null;
  sex?: string | null;
  /** Varias claves en el mismo PDF: los datos de cada una. */
  candidates?: CurpFacts[] | null;
}

/** Lo registrado (o lo que la familia escribió en el formulario). */
export interface CurpRegistered {
  first_name?: string | null;
  last_name?: string | null;
  maternal_last_name?: string | null;
  birthdate?: string | null;
  gender?: string | null;
}

export interface CurpClassification {
  status: CurpStatus;
  reasons: string[];
  notes: string[];
  /** Índice de la clave que mejor coincide (0 con una sola). */
  candidate: number;
}

// RENAPO: partículas que se saltan en nombres y apellidos compuestos.
const CURP_PARTICLES = new Set([
  "DA", "DAS", "DE", "DEL", "DER", "DI", "DIE", "DD", "EL", "LA", "LAS", "LE", "LES",
  "LOS", "MAC", "MC", "VAN", "VON", "Y",
]);
// Nombres de pila que RENAPO salta cuando hay otro nombre después.
const CURP_SKIPPED_GIVEN = new Set(["JOSE", "J", "MARIA", "MA", "M"]);
// «Ma.» es «María».
const ABBREVIATIONS: Record<string, string> = { MA: "MARIA" };
const VOWELS = new Set(["A", "E", "I", "O", "U"]);

const STATUS_RANK: Record<CurpStatus, number> = { matches: 0, similar: 1, other_person: 2, unreadable: 3 };
// Claves que se comparan de un mismo PDF, igual que MAX_CANDIDATES en Python
// (el servidor nunca guarda ni manda más).
export const MAX_CANDIDATES = 5;

// ---------------------------------------------------------------- texto

/** MAYÚSCULAS sin acentos ni puntuación. `enye="N"` para COMPARAR nombres
 * («Ibáñez» = «Ibanez»); `enye="X"` para las letras de la clave (RENAPO
 * escribe la Ñ como X). La Ñ se aparta ANTES de quitar acentos: la
 * descomposición la volvería N. */
export function fold(value: string | null | undefined, enye: "N" | "X" = "N"): string {
  const upper = String(value ?? "").toUpperCase().replace(/Ñ/g, "\u0000");
  const plain = upper.normalize("NFD").replace(/\p{Mn}/gu, "").replace(/\u0000/g, enye);
  return plain.replace(/[^A-Z ]+/g, " ");
}

/** Palabras de un nombre ya dobladas; «MA» cuenta como «MARIA». */
export function tokens(value: string | null | undefined, enye: "N" | "X" = "N"): string[] {
  return fold(value, enye)
    .split(" ")
    .filter(Boolean)
    .map((token) => ABBREVIATIONS[token] ?? token);
}

function noParticles(words: string[]): string[] {
  const kept = words.filter((word) => !CURP_PARTICLES.has(word));
  return kept.length ? kept : words;
}

/** Nombres de pila sin partículas y sin JOSE/MARIA cuando hay otro después. */
function significantGiven(words: string[]): string[] {
  let result = noParticles(words);
  if (result.length > 1 && CURP_SKIPPED_GIVEN.has(result[0])) result = result.slice(1);
  return noParticles(result);
}

function asTokens(value: string | readonly string[] | null | undefined, enye: "N" | "X"): string[] {
  if (value === null || value === undefined) return [];
  if (typeof value === "string") return tokens(value, enye);
  return tokens(value.join(" "), enye);
}

// ---------------------------------------------------------------- letras RENAPO

function internal(word: string, vowel: boolean): string {
  for (const ch of word.slice(1)) {
    if (VOWELS.has(ch) === vowel) return ch;
  }
  return "X";
}

function codeLettersFromTokens(given: string[], paternal: string[], maternal: string[]): string | null {
  const significant = significantGiven(given);
  const paternalWords = noParticles(paternal);
  const maternalWords = noParticles(maternal);
  if (!significant.length || !paternalWords.length) return null;
  const givenWord = significant[0];
  const paternalWord = paternalWords[0];
  const maternalWord = maternalWords.length ? maternalWords[0] : null;
  return (
    paternalWord[0] +
    internal(paternalWord, true) +
    (maternalWord ? maternalWord[0] : "X") +
    givenWord[0] +
    internal(paternalWord, false) +
    (maternalWord ? internal(maternalWord, false) : "X") +
    internal(givenWord, false)
  );
}

/** Las 7 letras RENAPO (posiciones 0,1,2,3,13,14,15) que le tocarían a ese
 * nombre. La Ñ cuenta como X. null sin nombre de pila o sin apellido paterno. */
export function codeLetters(
  given: string | readonly string[] | null | undefined,
  paternal: string | readonly string[] | null | undefined,
  maternal: string | readonly string[] | null | undefined = null,
): string | null {
  return codeLettersFromTokens(asTokens(given, "X"), asTokens(paternal, "X"), asTokens(maternal, "X"));
}

/** Las 7 letras del nombre dentro de una clave (o las mismas 7 letras). */
export function lettersOf(curpOrLetters: string | null | undefined): string | null {
  const value = String(curpOrLetters ?? "").trim().toUpperCase();
  if (value.length === 18) return value.slice(0, 4) + value.slice(13, 16);
  if (value.length === 7 && /^\p{L}+$/u.test(value)) return value;
  return null;
}

// ---------------------------------------------------------------- fecha y sexo

function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${String(date.getFullYear()).padStart(4, "0")}-${month}-${day}`;
}

/** Fecha de nacimiento (ISO) de la clave. El siglo lo dice el carácter 17
 * (regla RENAPO): letra = nacido en 2000 o después, dígito = antes de 2000.
 * null si la fecha no existe o es futura. */
export function birthdateFromCurp(curp: string | null | undefined, today: Date = new Date()): string | null {
  const value = String(curp ?? "").trim().toUpperCase();
  if (value.length < 17 || !/^\d{6}$/.test(value.slice(4, 10))) return null;
  const century = /\p{L}/u.test(value[16]) ? 2000 : 1900;
  const year = century + Number(value.slice(4, 6));
  const month = Number(value.slice(6, 8));
  const day = Number(value.slice(8, 10));
  const born = new Date(Date.UTC(year, month - 1, day));
  if (born.getUTCFullYear() !== year || born.getUTCMonth() !== month - 1 || born.getUTCDate() !== day) return null;
  const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return iso > localIsoDate(today) ? null : iso;
}

export function sexFromCurp(curp: string | null | undefined): "male" | "female" | null {
  const value = String(curp ?? "").trim().toUpperCase();
  if (value.length < 11) return null;
  return value[10] === "H" ? "male" : value[10] === "M" ? "female" : null;
}

// ---------------------------------------------------------------- comparación

/** Caracteres en común según difflib.SequenceMatcher de Python (sin basura;
 * mismo algoritmo: el bloque común más largo y luego recursión a los lados).
 * Así `ratio` da EXACTAMENTE lo mismo que en el backend. */
function matchingCharacters(a: string, b: string): number {
  const b2j = new Map<string, number[]>();
  for (let j = 0; j < b.length; j += 1) {
    const list = b2j.get(b[j]);
    if (list) list.push(j);
    else b2j.set(b[j], [j]);
  }
  // autojunk de difflib: solo aplica con 200 caracteres o más.
  if (b.length >= 200) {
    const limit = Math.floor(b.length / 100) + 1;
    for (const [ch, list] of [...b2j.entries()]) {
      if (list.length > limit) b2j.delete(ch);
    }
  }
  const longest = (alo: number, ahi: number, blo: number, bhi: number): [number, number, number] => {
    let besti = alo;
    let bestj = blo;
    let bestsize = 0;
    let j2len = new Map<number, number>();
    for (let i = alo; i < ahi; i += 1) {
      const next = new Map<number, number>();
      for (const j of b2j.get(a[i]) ?? []) {
        if (j < blo) continue;
        if (j >= bhi) break;
        const k = (j2len.get(j - 1) ?? 0) + 1;
        next.set(j, k);
        if (k > bestsize) {
          besti = i - k + 1;
          bestj = j - k + 1;
          bestsize = k;
        }
      }
      j2len = next;
    }
    while (besti > alo && bestj > blo && a[besti - 1] === b[bestj - 1]) {
      besti -= 1;
      bestj -= 1;
      bestsize += 1;
    }
    while (besti + bestsize < ahi && bestj + bestsize < bhi && a[besti + bestsize] === b[bestj + bestsize]) {
      bestsize += 1;
    }
    return [besti, bestj, bestsize];
  };
  let matches = 0;
  const queue: Array<[number, number, number, number]> = [[0, a.length, 0, b.length]];
  while (queue.length) {
    const [alo, ahi, blo, bhi] = queue.pop() as [number, number, number, number];
    const [i, j, k] = longest(alo, ahi, blo, bhi);
    if (k) {
      matches += k;
      if (alo < i && blo < j) queue.push([alo, i, blo, j]);
      if (i + k < ahi && j + k < bhi) queue.push([i + k, ahi, j + k, bhi]);
    }
  }
  return matches;
}

/** `SequenceMatcher(None, a, b).ratio()`. */
export function sequenceRatio(a: string, b: string): number {
  const total = a.length + b.length;
  return total ? (2 * matchingCharacters(a, b)) / total : 1;
}

/** Misma palabra mal escrita («JOHANA» / «JOHANNA»). */
function nearWord(a: string, b: string): boolean {
  return Math.min(a.length, b.length) >= 4 && a[0] === b[0] && sequenceRatio(a, b) >= 0.85;
}

function isoDate(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === "") return null;
  return String(value).trim().slice(0, 10) || null;
}

function sameWords(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((word, index) => word === right[index]);
}

function result(status: CurpStatus, reasons: string[], notes: string[] = []): Omit<CurpClassification, "candidate"> {
  return { status, reasons, notes: [...notes] };
}

function classifyCandidate(candidate: CurpFacts, registered: CurpRegistered): Omit<CurpClassification, "candidate"> {
  // 1) Fecha de nacimiento: si las dos están y no coinciden, es otra persona.
  const curpBirthdate = isoDate(candidate.birthdate);
  const registeredBirthdate = isoDate(registered.birthdate);
  if (curpBirthdate && registeredBirthdate && curpBirthdate !== registeredBirthdate) {
    return result("other_person", ["birthdate_differs"]);
  }
  const curpSex = candidate.sex;
  const gender = registered.gender;
  const sexDiffers =
    (gender === "male" || gender === "female") && (curpSex === "male" || curpSex === "female") && gender !== curpSex;

  const registeredGivenAll = noParticles(tokens(registered.first_name));
  const registeredGiven = significantGiven(tokens(registered.first_name));
  const registeredPaternal = noParticles(tokens(registered.last_name));
  const registeredMaternal = noParticles(tokens(registered.maternal_last_name));
  // 2) El registro no trae nombre o apellido: no hay con qué comparar.
  if (!registeredGiven.length || !registeredPaternal.length) {
    return result("similar", ["registered_name_incomplete"]);
  }

  const name = candidate.name ?? null;
  const curpGivenWords = tokens(name?.given);
  const curpPaternalWords = tokens(name?.paternal);
  const reasons: string[] = [];
  const notes: string[] = [];

  if (!curpGivenWords.length || !curpPaternalWords.length) {
    // 3) Sin renglón de nombre confiable: contra las letras de la clave.
    const actual = lettersOf(candidate.letters) ?? "";
    const expected = codeLetters(registered.first_name, registered.last_name, registered.maternal_last_name);
    if (actual.length !== 7 || !expected) return result("similar", ["name_not_readable"]);
    if (expected[3] !== actual[3]) return result("other_person", ["given_initial_differs"]);
    if (expected[0] !== actual[0]) {
      if (expected[2] !== actual[2]) return result("other_person", ["paternal_initial_differs"]);
      reasons.push("paternal_initial_differs");
    }
    if (expected[6] !== actual[6]) reasons.push("given_consonant_differs");
    if (registeredMaternal.length && (expected[2] !== actual[2] || expected[5] !== actual[5])) {
      reasons.push("maternal_differs");
    }
    if (sexDiffers) reasons.push("sex_differs");
    notes.push("name_from_code_only");
    return result(reasons.length ? "similar" : "matches", reasons, notes);
  }

  const curpGivenAll = noParticles(curpGivenWords);
  const curpGiven = significantGiven(curpGivenWords);
  const curpPaternal = noParticles(curpPaternalWords);
  const curpMaternal = name?.maternal ? noParticles(tokens(name.maternal)) : [];

  // 4) Primer nombre de pila (regla JOSE/MARIA).
  if (registeredGiven[0] !== curpGiven[0]) {
    const curpGivenSet = new Set(curpGivenAll);
    if (registeredGivenAll.every((word) => curpGivenSet.has(word))) {
      reasons.push("uses_other_given_name"); // «Rafael» por «BRUNO RAFAEL»
    } else if (nearWord(registeredGiven[0], curpGiven[0]) && !sexDiffers) {
      reasons.push("given_name_spelling"); // «Johana» / «JOHANNA»
    } else {
      return result("other_person", ["given_name_differs"]);
    }
  }
  // 5) Apellido paterno.
  if (registeredPaternal[0] !== curpPaternal[0]) {
    const curpSurnames = new Set([...curpPaternal, ...curpMaternal]);
    if (nearWord(registeredPaternal[0], curpPaternal[0])) {
      reasons.push("paternal_spelling");
    } else if ([...registeredPaternal, ...registeredMaternal].some((word) => curpSurnames.has(word))) {
      reasons.push("surnames_reordered");
    } else {
      return result("other_person", ["surnames_differ"]);
    }
  }
  // 6) Diferencias menores: parecido.
  if (
    !sameWords(registeredGivenAll, curpGivenAll) &&
    !reasons.includes("uses_other_given_name") &&
    !reasons.includes("given_name_spelling")
  ) {
    reasons.push("given_names_differ"); // falta o sobra un nombre
  }
  if (
    !sameWords(registeredPaternal, curpPaternal) &&
    !reasons.includes("paternal_spelling") &&
    !reasons.includes("surnames_reordered")
  ) {
    reasons.push("paternal_differs");
  }
  if (registeredMaternal.length && !sameWords(registeredMaternal, curpMaternal)) reasons.push("maternal_differs");
  if (!registeredMaternal.length && curpMaternal.length) notes.push("maternal_not_registered"); // opcional: solo nota
  if (sexDiffers) reasons.push("sex_differs");
  return result(reasons.length ? "similar" : "matches", reasons, notes);
}

/** Compara lo que dice la CURP contra lo registrado (o lo escrito). */
export function classifyCurp(facts: CurpFacts | null | undefined, registered: CurpRegistered): CurpClassification {
  if (!facts || facts.reading !== "ok") {
    return { status: "unreadable", reasons: [facts?.reason || "no_curp"], notes: [], candidate: 0 };
  }
  const listed = (facts.candidates ?? [])
    .filter((item): item is CurpFacts => Boolean(item) && typeof item === "object")
    .slice(0, MAX_CANDIDATES);
  const candidates = listed.length ? listed : [facts];
  // La mejor de las claves (gana la primera en empate, como en Python).
  let best: CurpClassification = { ...classifyCandidate(candidates[0], registered), candidate: 0 };
  for (let index = 1; index < candidates.length; index += 1) {
    const found = { ...classifyCandidate(candidates[index], registered), candidate: index };
    if (STATUS_RANK[found.status] < STATUS_RANK[best.status]) best = found;
  }
  if (candidates.length > 1) best.notes.push("multiple_curps");
  return best;
}

// ---------------------------------------------------------------- formulario público

type CurpParseData = PublicCurpParseResponse["data"];

/** Lo que dice el PDF según la respuesta de parse-curp. null si no trae clave
 * (el servidor responde 422 en ese caso, así que casi nunca pasa). */
export function curpFactsFromParse(data: CurpParseData | null | undefined): CurpFacts | null {
  const curp = data?.parsed?.curp;
  if (!data || !curp) return null;
  const split = data.name_split ?? data.parsed.name_split ?? null;
  const name =
    split && (split.first_name || split.last_name)
      ? { given: split.first_name ?? null, paternal: split.last_name ?? null, maternal: split.maternal_last_name ?? null }
      : null;
  const gender = data.parsed.gender;
  return {
    reading: "ok",
    reason: null,
    name,
    letters: lettersOf(curp),
    birthdate: data.parsed.birthdate ?? birthdateFromCurp(curp),
    sex: gender === "male" || gender === "female" ? gender : sexFromCurp(curp),
  };
}

/** Comparación sin acentos, mayúsculas ni puntuación («Núñez» = «NUNEZ»). */
export function sameNameText(left: string | null | undefined, right: string | null | undefined): boolean {
  return tokens(left).join(" ") === tokens(right).join(" ");
}

/** Firma de «lo que dice la CURP» + «lo escrito». «El nombre está bien,
 * continuar» guarda esta firma: si después cambia el nombre, la fecha o el
 * género (o llega otra CURP), el aviso vuelve a abrirse. */
export function curpReviewSignature(facts: CurpFacts | null | undefined, registered: CurpRegistered): string | null {
  if (!facts) return null;
  return [
    facts.letters ?? "",
    facts.birthdate ?? "",
    tokens(registered.first_name).join(" "),
    tokens(registered.last_name).join(" "),
    tokens(registered.maternal_last_name).join(" "),
    isoDate(registered.birthdate) ?? "",
    registered.gender ?? "",
  ].join("|");
}
